import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-2xl md:text-3xl font-black mb-4 text-white tracking-tight">
      {title}
    </h2>
    <div className="text-zinc-200 leading-relaxed space-y-3 text-sm md:text-base">{children}</div>
  </section>
);

const StaticShell = ({ title, updated, children }) => (
  <div className="min-h-screen bg-black text-white">
    <header className="border-b border-zinc-800">
      <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-mono text-[11px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white">
          ← Money/Hub
        </Link>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-300">
          Updated {updated}
        </div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight mb-10">{title}</h1>
      {children}
    </main>
    <footer className="border-t border-zinc-800 mt-8">
      <div className="max-w-3xl mx-auto px-6 py-8 text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono flex flex-wrap gap-4">
        <Link to="/">Home</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/dpa">DPA</Link>
        <Link to="/refund">Refund</Link>
        <Link to="/msa">MSA</Link>
        <Link to="/insurance">Insurance</Link>
        <span className="text-zinc-400">© {new Date().getFullYear()} NC Construction LLC × Clark Properties</span>
      </div>
    </footer>
  </div>
);

export function PrivacyPage() {
  return (
    <StaticShell title="Privacy Policy" updated="Feb 2026">
      <Section title="Overview">
        <p>
          Money/Hub is operated by <b className="text-white">NC Construction LLC</b> and{" "}
          <b className="text-white">Clark Properties</b>. This policy explains what data we collect,
          why, and how you can access or delete it at any time. If anything here is unclear, email{" "}
          <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">
            moneyhubvip@outlook.com
          </a>.
        </p>
      </Section>

      <Section title="What we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><b className="text-white">Account:</b> Google-supplied email, name, and profile picture URL (via OAuth).</li>
          <li><b className="text-white">Preferences:</b> alert thresholds, alternate email, Cash App handle, and payment settings.</li>
          <li><b className="text-white">Activity:</b> opportunities you save, action plans you generate, earnings you log, and platform-fee transactions.</li>
          <li><b className="text-white">Logs:</b> alert send/skip records used strictly for de-duping and rate limits.</li>
        </ul>
        <p className="mt-3">
          We do <b className="text-white">not</b> collect: bank account numbers, credit-card numbers, browsing outside
          Money/Hub, cross-site tracking, or third-party ad IDs. Payments run through Stripe and
          PayPal; card data never touches our servers.
        </p>
      </Section>

      <Section title="Isolation & security">
        <p>
          Every record you create is tagged with your account&apos;s internal <code>user_id</code>. Every
          API endpoint enforces this scope — no user can query or modify another user&apos;s data. Session
          cookies are HttpOnly, SameSite=Lax, and rotated on each sign-in. Rate limits protect against
          abuse. Passwords do not exist here — we rely on Google&apos;s OAuth verification.
        </p>
      </Section>

      <Section title="Your data, your rules">
        <ul className="list-disc pl-5 space-y-1">
          <li><b className="text-white">Export:</b> download a JSON copy of everything we hold about you from the dashboard.</li>
          <li><b className="text-white">Delete:</b> one-click erase — removes your account, saved items, alert history, earnings log, and payment records. Deletion is permanent and immediate.</li>
          <li><b className="text-white">Retention:</b> we keep data only while your account is active. If you delete, records are wiped from our primary MongoDB within seconds.</li>
        </ul>
      </Section>

      <Section title="Third parties">
        <ul className="list-disc pl-5 space-y-1">
          <li><b className="text-white">Google OAuth</b> — sign-in only.</li>
          <li><b className="text-white">Stripe / PayPal</b> — platform-fee payments only; both are PCI-compliant.</li>
          <li><b className="text-white">Resend / SMTP</b> — used to send alert emails you opt into.</li>
          <li><b className="text-white">CoinGecko</b> — public crypto price data; no personal info sent.</li>
          <li><b className="text-white">Claude (via Emergent LLM)</b> — used to generate side-hustle ideas and action plans; prompt content is sent but not tied to your identity.</li>
        </ul>
      </Section>

      <Section title="Contact">
        <p>
          Data requests, deletion appeals, or privacy questions:{" "}
          <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">
            moneyhubvip@outlook.com
          </a>
        </p>
      </Section>
    </StaticShell>
  );
}

export function TermsPage() {
  return (
    <StaticShell title="Terms of Service" updated="Feb 2026">
      <Section title="Who we are">
        <p>
          Money/Hub is a software service operated by <b className="text-white">NC Construction LLC</b>{" "}
          and <b className="text-white">Clark Properties</b> (&quot;we&quot;, &quot;us&quot;). By creating an account
          you agree to these terms.
        </p>
      </Section>

      <Section title="What Money/Hub does">
        <p>
          Money/Hub aggregates public information about freelance gigs, dropship products, crypto
          prices, and paid micro-tasks. We rank them using AI and let you save the ones you like.
          We are <b className="text-white">not</b> a broker, employer, or payment processor. Any
          money you earn is earned directly from the third-party platform you found through Money/Hub.
        </p>
      </Section>

      <Section title="Platform fee">
        <p>
          Money/Hub charges a flat <b className="text-white">20%</b> platform fee on income you self-report
          as having earned through opportunities discovered here. You log the earnings in the
          dashboard&apos;s Earnings Ledger; the app calculates 20% and lets you pay via Stripe or PayPal.
          Fees are non-refundable once collected. You are responsible for reporting your gross
          earnings for tax purposes.
        </p>
      </Section>

      <Section title="Acceptable use">
        <ul className="list-disc pl-5 space-y-1">
          <li>No automated scraping of our API beyond the built-in rate limits.</li>
          <li>No fraudulent earnings logs, false chargebacks, or reversal abuse.</li>
          <li>No use of Money/Hub to promote illegal, harmful, or misleading opportunities.</li>
          <li>One account per person. Multi-accounting to dodge fees is a breach.</li>
        </ul>
      </Section>

      <Section title="No warranty">
        <p>
          Money/Hub is provided <b className="text-white">as-is</b>. Opportunity rankings are AI
          estimates, not guarantees. We are not liable for any lost income, missed gigs, or
          third-party platform disputes. Crypto and dropship data can lag; verify before acting.
        </p>
      </Section>

      <Section title="Termination">
        <p>
          You may delete your account and all associated data at any time from the dashboard. We
          may suspend accounts that violate these terms. Deletion is permanent.
        </p>
      </Section>

      <Section title="Governing law">
        <p>
          These terms are governed by the laws of the State of North Carolina, USA. Disputes are
          resolved in state courts of Cherokee County, NC.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">
            moneyhubvip@outlook.com
          </a>
        </p>
      </Section>
    </StaticShell>
  );
}
